/**
 * Login page. Collects email + password, validates client-side, then delegates
 * to the auth store. On success it redirects to the originally requested route
 * (or the dashboard). Already-authenticated users are bounced straight in.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Input from '@/components/Forms/Input';
import Button from '@/components/Forms/Button';
import Alert from '@/components/Alerts/Alert';
import { useToast } from '@/components/Alerts/toastContext';
import { useAuthStore } from '@/store/authStore';
import { validateEmail, validatePassword } from '@/utils/validators';
import Logo from '@/components/common/Logo';

interface LocationState {
  from?: { pathname?: string };
}

/** Email/password sign-in form. */
export default function Login(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/';

  // Prevent showing the form to an authenticated user.
  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const validate = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    if (!validateEmail(email)) errors.email = 'Email không hợp lệ';
    const pwdError = validatePassword(password);
    if (pwdError) errors.password = pwdError;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await login(email.trim(), password);
      toast.success('Đăng nhập thành công');
      navigate(redirectTo, { replace: true });
    } catch {
      // The store already captured a user-facing message in `error`.
      toast.error('Đăng nhập thất bại');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Logo className="mx-auto mb-3 h-14 w-14" />
          <h1 className="text-2xl font-bold text-gray-900">Ads360</h1>
          <p className="mt-1 text-sm text-gray-500">Đăng nhập để quản lý quảng cáo của bạn</p>
        </div>

        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Đăng nhập</h2>

          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="ban@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldErrors.email}
              required
            />
            <Input
              label="Mật khẩu"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
              required
            />

            <div className="flex justify-end">
              <span className="cursor-not-allowed text-sm text-gray-400" title="Chưa hỗ trợ">
                Quên mật khẩu?
              </span>
            </div>

            <Button type="submit" loading={loading} className="w-full">
              Đăng nhập
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Chưa có tài khoản?{' '}
            <Link to="/register" className="font-medium text-brand-600 hover:text-brand-700">
              Đăng ký ngay
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
